import { redactSensitiveText } from './redaction';

export type ConsoleLevel = 'debug' | 'error' | 'info' | 'log' | 'warn';

export type ConsoleEntry = {
  level: ConsoleLevel;
  timestamp: string;
  message: string;
};

type ConsoleMethod = (...args: unknown[]) => void;

const CAPTURED_LEVELS = ['debug', 'error', 'info', 'log', 'warn'] as const;

const DEFAULT_ENTRY_LIMIT = 200;

const MAX_MESSAGE_LENGTH = 2000;

let entries: ConsoleEntry[] = [];
let droppedEntries = 0;
let entryLimit = DEFAULT_ENTRY_LIMIT;
let uninstallCapture: (() => void) | null = null;

function safeToString(value: unknown): string {
  try {
    return String(value);
  } catch {
    return '[unstringifiable value]';
  }
}

function describeFunction(value: ConsoleMethod): string {
  return value.name === '' ? '[Function (anonymous)]' : `[Function: ${value.name}]`;
}

function describeObject(value: object): string {
  const seen = new WeakSet<object>();

  try {
    const json = JSON.stringify(value, (_key, nested: unknown) => {
      if (typeof nested !== 'object' || nested === null) {
        return nested;
      }
      if (seen.has(nested)) {
        return '[Circular]';
      }
      seen.add(nested);
      return nested;
    });

    return json ?? safeToString(value);
  } catch {
    return safeToString(value);
  }
}

function formatArgument(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value === undefined) {
    return 'undefined';
  }
  if (value === null) {
    return 'null';
  }
  if (value instanceof Error) {
    return value.stack ?? `${value.name}: ${value.message}`;
  }
  if (typeof value === 'function') {
    return describeFunction(value as ConsoleMethod);
  }
  if (typeof value === 'object') {
    return describeObject(value);
  }
  return safeToString(value);
}

export function formatConsoleArguments(args: readonly unknown[]): string {
  const message = redactSensitiveText(args.map(formatArgument).join(' '));

  return message.length <= MAX_MESSAGE_LENGTH
    ? message
    : `${message.slice(0, MAX_MESSAGE_LENGTH)}… (${message.length - MAX_MESSAGE_LENGTH} more characters)`;
}

function recordIgnoringFailures(level: ConsoleLevel, args: readonly unknown[]): void {
  try {
    if (args.length === 0) {
      return;
    }

    entries.push({
      level,
      timestamp: new Date().toISOString(),
      message: formatConsoleArguments(args),
    });

    if (entries.length > entryLimit) {
      droppedEntries += entries.length - entryLimit;
      entries.splice(0, entries.length - entryLimit);
    }
  } catch {
    return;
  }
}

export function installConsoleCapture(options: { limit?: number } = {}): () => void {
  if (uninstallCapture !== null) {
    return uninstallCapture;
  }

  entryLimit = options.limit ?? DEFAULT_ENTRY_LIMIT;

  const target = console as unknown as Record<ConsoleLevel, ConsoleMethod>;
  const originals = new Map<ConsoleLevel, ConsoleMethod>();

  for (const level of CAPTURED_LEVELS) {
    const original = target[level];
    originals.set(level, original);
    target[level] = (...args: unknown[]) => {
      recordIgnoringFailures(level, args);
      original.apply(console, args);
    };
  }

  uninstallCapture = () => {
    for (const [level, original] of originals) {
      target[level] = original;
    }
    uninstallCapture = null;
  };

  return uninstallCapture;
}

export function isConsoleCaptureInstalled(): boolean {
  return uninstallCapture !== null;
}

export function getConsoleEntries(): readonly ConsoleEntry[] {
  return entries;
}

export function getDroppedConsoleEntryCount(): number {
  return droppedEntries;
}

export function clearConsoleEntries(): void {
  entries = [];
  droppedEntries = 0;
}
